
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2, AlertCircle, RefreshCw } from 'lucide-react';
import { DashboardHeader } from './components/DashboardHeader';
import { ResponsesPanel } from './components/ResponsesPanel';
import { EmptyState } from './components/EmptyState';
import { DashboardNavigation } from './components/DashboardNavigation';
import { useDashboard } from './hooks/useDashboard';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';

const Dashboard: React.FC = () => {
  const { 
    loading, 
    savedResponses,
    filteredResponses,
    activeTab, 
    setActiveTab,
    selectedItems, 
    setSelectedItems,
    isDeleteModalOpen, 
    setIsDeleteModalOpen,
    handleDelete,
    handleMultipleDelete,
    toggleItemSelection,
    selectAllItems,
    error,
    refetch
  } = useDashboard();

  const [loadingTimeout, setLoadingTimeout] = useState(false);
  
  // Handle loading timeout
  useEffect(() => {
    let timer: NodeJS.Timeout;
    
    if (loading) {
      timer = setTimeout(() => {
        setLoadingTimeout(true);
      }, 20000); // Set timeout after 20 seconds of loading
    } else {
      setLoadingTimeout(false);
    }
    
    return () => clearTimeout(timer);
  }, [loading]);

  if (error) {
    return (
      <div className="bg-white min-h-screen pt-24">
        <div className="container mx-auto px-4 py-8">
          <Alert variant="destructive" className="mb-6 border-red-500 text-red-700 bg-red-50">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              {error}
            </AlertDescription>
          </Alert>
          <div className="flex justify-center mt-4">
            <Button 
              variant="gold"
              onClick={refetch}
              className="flex items-center gap-2"
            >
              <RefreshCw className="h-4 w-4" />
              Retry Loading Dashboard
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white min-h-screen">
      <DashboardHeader />

      <div className="container mx-auto px-4 py-8 bg-white">
        <DashboardNavigation 
          activeTab={activeTab}
          onTabChange={setActiveTab}
        />

        {loading ? (
          <div className="flex flex-col justify-center items-center py-12 bg-white">
            <Loader2 className="h-12 w-12 animate-spin text-heaven-gold mb-4" />
            <p className="text-gray-600">
              {loadingTimeout 
                ? "This is taking longer than usual. Please wait a moment..." 
                : "Loading your saved responses..."}
            </p>
            {loadingTimeout && (
              <Button 
                variant="outline"
                onClick={refetch}
                className="mt-4 flex items-center gap-2"
              >
                <RefreshCw className="h-4 w-4" />
                Retry
              </Button>
            )}
          </div>
        ) : filteredResponses.length > 0 ? (
          <ResponsesPanel 
            filteredResponses={filteredResponses}
            selectedItems={selectedItems}
            setSelectedItems={setSelectedItems}
            selectAllItems={() => selectAllItems(selectedItems.length !== filteredResponses.length || filteredResponses.length === 0)}
            toggleItemSelection={toggleItemSelection}
            handleDelete={handleDelete}
            handleMultipleDelete={handleMultipleDelete}
            isDeleteModalOpen={isDeleteModalOpen}
            setIsDeleteModalOpen={setIsDeleteModalOpen}
          />
        ) : (
          <EmptyState activeTab={activeTab} />
        )}
      </div>
    </div>
  );
};

export default Dashboard;
